(function () {
  const SECTION_CLASS = "feature-hotspots-section";
  const ROOT_SELECTOR = ".feature-hotspots";
  const OPEN_CLASS = "is-open";
  const FLIPPED_CLASS = "is-flipped";

  // Desktop (fine pointer) opens on hover and the cross is decorative; coarse pointers
  // toggle on the cross itself. Evaluated per event so a hybrid device stays correct.
  const hoverQuery = window.matchMedia("(hover: hover) and (pointer: fine)");

  class FeatureHotspots {
    constructor(el) {
      this.el = el;
      this.controller = new AbortController();
      this.markers = Array.from(el.querySelectorAll("[data-feature-hotspot]"));

      if (!this.markers.length) return;

      const signal = this.controller.signal;
      this.hovered = null;

      // Hover follows real pointer movement only. The open card's edge and a shifted toggle
      // move under a resting cursor, and Chrome answers that with mouseenter/mouseleave but
      // no mousemove — on the cross's border that looped open/close without end.
      el.addEventListener(
        "mousemove",
        (event) => {
          if (!hoverQuery.matches) return;

          const target = event.target;
          const marker = target instanceof Element ? target.closest("[data-feature-hotspot]") : null;
          this.setHovered(marker && this.markers.includes(marker) ? marker : null);
        },
        { signal }
      );

      el.addEventListener(
        "mouseleave",
        () => {
          if (hoverQuery.matches) this.setHovered(null);
        },
        { signal }
      );

      this.markers.forEach((marker) => {
        const toggle = marker.querySelector("[data-feature-hotspot-toggle]");
        if (!toggle) return;

        // Keyboard focus only — a pointer press also fires focusin and would fight the click toggle.
        marker.addEventListener(
          "focusin",
          (event) => {
            const focused = event.target;
            if (!(focused instanceof Element)) return;

            try {
              if (focused.matches(":focus-visible")) this.open(marker);
            } catch (error) {
              /* :focus-visible unsupported — hover and click still cover it */
            }
          },
          { signal }
        );

        marker.addEventListener(
          "focusout",
          (event) => {
            if (!marker.contains(event.relatedTarget)) this.close(marker);
          },
          { signal }
        );

        toggle.addEventListener(
          "click",
          () => {
            if (hoverQuery.matches) return;

            if (marker.classList.contains(OPEN_CLASS)) {
              this.close(marker);
            } else {
              this.open(marker);
            }
          },
          { signal }
        );
      });

      document.addEventListener(
        "click",
        (event) => {
          if (hoverQuery.matches) return;

          const target = event.target;
          if (target instanceof Element && target.closest("[data-feature-hotspot]")) return;

          this.closeAll();
        },
        { signal }
      );

      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape") this.closeAll();
        },
        { signal }
      );
    }

    setHovered(marker) {
      if (marker === this.hovered) return;

      const previous = this.hovered;
      this.hovered = marker;

      if (marker) {
        this.open(marker);
      } else if (previous) {
        this.close(previous);
      }
    }

    open(marker) {
      this.closeAll(marker);
      this.measureText(marker);
      this.orient(marker);
      marker.classList.add(OPEN_CLASS);

      const toggle = marker.querySelector("[data-feature-hotspot-toggle]");
      if (toggle) toggle.setAttribute("aria-expanded", "true");
    }

    // Each text hides a full own height below its clip. The offset is swapped with no
    // transition, so the rise starts from it rather than animating to it first.
    measureText(marker) {
      // Unchanged offsets are left alone: a card reopened mid-close keeps its running
      // transition and reverses, instead of the text snapping down first.
      const stale = Array.from(marker.querySelectorAll(".feature-hotspots__marker-text"))
        .map((text) => ({ text, offset: `${text.getBoundingClientRect().height}px` }))
        .filter(({ text, offset }) => text.style.getPropertyValue("--fh-text-offset") !== offset);
      if (!stale.length) return;

      stale.forEach(({ text, offset }) => {
        text.style.transition = "none";
        text.style.setProperty("--fh-text-offset", offset);
      });
      void stale[0].text.offsetHeight;
      stale.forEach(({ text }) => {
        text.style.transition = "";
      });
    }

    // The open card reaches `content height + half a toggle` past the point. It grows upward
    // unless that overflows the media top — then downward, or towards the roomier side when
    // neither fits, and `--fh-shift` slides it back inside by whatever still overflows.
    // A card taller than the media (a landscape image on a phone) may spill into the
    // section's free room — its padding, the gap under the heading — but never past it;
    // with no room at all it centres on the media. Only a card that fits keeps the toggle
    // inside the media: a spilling one carries the cross on its own edge, over the card.
    orient(marker) {
      const layer = marker.closest(".feature-hotspots__markers");
      const card = marker.querySelector(".feature-hotspots__marker-card");
      const content = marker.querySelector(".feature-hotspots__marker-content");
      const toggle = marker.querySelector("[data-feature-hotspot-toggle]");
      if (!layer || !card || !content || !toggle) return;

      const layerRect = layer.getBoundingClientRect();
      const toggleRect = toggle.getBoundingClientRect();
      const point = toggleRect.top + toggleRect.height / 2 - layerRect.top;
      const needed = content.getBoundingClientRect().height + toggleRect.height / 2;
      const above = point;
      const below = layerRect.height - point;
      const flip = above < needed && below > above;

      const half = toggleRect.height / 2;
      const tail = half - (parseFloat(getComputedStyle(card).getPropertyValue("--fh-open-lift")) || 0) * this.remPx();
      const top = flip ? point - tail : point - needed;
      const bottom = flip ? point + needed : point + tail;

      const into = (lo, hi) => {
        if (bottom - top > hi - lo) return null;
        if (top < lo) return lo - top;
        if (bottom > hi) return hi - bottom;
        return 0;
      };
      const roomTop = this.roomTop(layer) - layerRect.top;
      const roomBottom = this.el.getBoundingClientRect().bottom - layerRect.top;

      let shift = into(0, layerRect.height);
      if (shift !== null) {
        shift = Math.min(Math.max(shift, half - point), layerRect.height - half - point);
      } else {
        shift = into(Math.min(0, roomTop), Math.max(layerRect.height, roomBottom));
        if (shift === null) shift = layerRect.height / 2 - (top + bottom) / 2;
      }
      marker.style.setProperty("--fh-shift", `${shift}px`);

      if (flip === marker.classList.contains(FLIPPED_CLASS)) return;

      // Closed, both anchors give the same box — swap it with no transition so it holds still.
      card.style.transition = "none";
      marker.classList.toggle(FLIPPED_CLASS, flip);
      void card.offsetHeight;
      card.style.transition = "";
    }

    // The spill may reach the section's top edge, or stop under the heading group so it
    // never covers the heading.
    roomTop(layer) {
      const stage = layer.closest(".feature-hotspots__stage");
      const before = stage && stage.previousElementSibling;
      if (before) return before.getBoundingClientRect().bottom;
      return this.el.getBoundingClientRect().top;
    }

    remPx() {
      return parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    }

    close(marker) {
      if (marker === this.hovered) this.hovered = null;
      marker.classList.remove(OPEN_CLASS);

      const toggle = marker.querySelector("[data-feature-hotspot-toggle]");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
    }

    closeAll(except) {
      this.markers.forEach((marker) => {
        if (marker !== except) this.close(marker);
      });
    }

    destroy() {
      this.controller.abort();
      this.closeAll();
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector(ROOT_SELECTOR);
    if (!root) return;

    if (root._featureHotspotsInstance) {
      root._featureHotspotsInstance.destroy();
      root._featureHotspotsInstance = null;
    }

    root._featureHotspotsInstance = new FeatureHotspots(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeFeatureHotspotsEvents) {
    window.__themeFeatureHotspotsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
        if (!el._featureHotspotsInstance) return;

        el._featureHotspotsInstance.destroy();
        el._featureHotspotsInstance = null;
      });
    });
  }
})();
